import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Crown, Check, Lock, Sparkles, Gift } from "lucide-react";
import { toast } from "sonner";
import { ReferralInvite } from "@/components/ReferralInvite";

const PERKS = [
  "Season Stats leaderboards — scorers, assists, clean sheets & cards",
  "Squads with real rosters + your own player ratings",
  "Win probabilities on every fixture",
  "Advanced \"Any venue\" Head-to-Head",
  "Unlimited Banter mini-leagues",
];

function RedeemCode({ onRedeemed }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/redeem", { code: code.trim() });
      toast.success("Code redeemed — you've got Premium for life! 🎉");
      setCode("");
      onRedeemed?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't redeem that code.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-4 border-2 border-dashed border-[hsl(var(--pitch-green))]/50 p-3" data-testid="redeem-form">
      <p className="overline text-muted-foreground flex items-center gap-1.5 mb-2">
        <Gift size={13} className="text-[hsl(var(--pitch-green))]" /> Got a free-access code?
      </p>
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="GAFFER-XXXXXX" data-testid="redeem-input"
          className="rounded-none font-data uppercase tracking-widest" />
        <Button type="submit" disabled={busy} data-testid="redeem-submit"
          className="rounded-none bg-[hsl(var(--pitch-green))] text-black hover:bg-[hsl(var(--pitch-green))]/90 uppercase text-xs font-bold">
          {busy ? "…" : "Redeem"}
        </Button>
      </div>
    </form>
  );
}

export function PremiumDialog({ open, onOpenChange }) {
  const { refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (open) api.get("/payments/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, [open]);

  const buy = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
      setBusy("");
    }
  };

  const fmt = (p) => `$${p.amount.toFixed(2)}${p.interval ? `/${p.interval}` : ""}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-2 border-[hsl(var(--stadium-yellow))] rounded-none max-w-lg" data-testid="premium-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase flex items-center gap-2">
            <Crown className="text-[hsl(var(--stadium-yellow))]" size={26} /> GAFFER Premium
          </DialogTitle>
          <DialogDescription className="font-data text-xs">
            Unlock the full prediction desk. Cancel anytime.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 my-2">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm">
              <Check size={15} className="text-[hsl(var(--pitch-green))] mt-0.5 shrink-0" /> {p}
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          {plans.map((p) => (
            <button key={p.lookup_key} onClick={() => buy(p.lookup_key)} disabled={!!busy}
              data-testid={`premium-buy-${p.plan}`}
              className={`border-2 p-4 text-left transition-colors disabled:opacity-60 ${
                p.plan === "season" ? "border-[hsl(var(--stadium-yellow))] bg-[hsl(var(--stadium-yellow))]/10" : "border-border hover:border-primary/60"}`}>
              <p className="overline text-muted-foreground">{p.label}{p.plan === "season" && " · best value"}</p>
              <p className="font-display text-3xl mt-1">{fmt(p)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {p.plan === "season" ? "One payment, all season" : "Billed monthly"}
              </p>
              <span className="inline-block mt-3 text-xs font-bold uppercase tracking-wider text-primary">
                {busy === p.lookup_key ? "Redirecting…" : "Choose →"}
              </span>
            </button>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground font-data mt-2">Secure checkout by Stripe · test mode</p>
        <RedeemCode onRedeemed={() => { refreshUser?.(); onOpenChange(false); }} />
        <ReferralInvite />
      </DialogContent>
    </Dialog>
  );
}

export function PremiumButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const freeForAll = user?.premium_free_for_all;
  if (user?.is_premium) {
    return (
      <>
        <button data-testid="premium-badge" onClick={() => setRefOpen(true)}
          title={freeForAll ? "Everything's unlocked free in early access — invite a mate" : "You're Premium — invite a mate to gift you both a free week"}
          className="inline-flex items-center gap-1 px-2 py-1 border border-[hsl(var(--stadium-yellow))] text-[hsl(var(--stadium-yellow))] text-[10px] uppercase tracking-wider hover:bg-[hsl(var(--stadium-yellow))]/10 transition-colors">
          <Crown size={12} /> {freeForAll ? "All Access · Free" : "Premium"}
        </button>
        <Dialog open={refOpen} onOpenChange={setRefOpen}>
          <DialogContent className="bg-card border-2 border-[hsl(var(--stadium-yellow))] rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl uppercase flex items-center gap-2">
                <Crown className="text-[hsl(var(--stadium-yellow))]" size={24} /> {freeForAll ? "Everything's unlocked" : "You're Premium"}
              </DialogTitle>
              <DialogDescription className="font-data text-xs">
                {freeForAll
                  ? "Every Premium feature is free while we're in early access. Help us grow — bring your mates in:"
                  : "Thanks for backing GAFFER. Spread the word:"}
              </DialogDescription>
            </DialogHeader>
            <ReferralInvite />
          </DialogContent>
        </Dialog>
      </>
    );
  }
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="go-premium-btn"
        className="bg-[hsl(var(--stadium-yellow))] text-black hover:bg-[hsl(var(--stadium-yellow))]/90 rounded-none uppercase text-xs font-bold tracking-wide">
        <Crown size={14} className="me-1" /> Go Premium
      </Button>
      <PremiumDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export function PremiumLock({ title, blurb }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-card border-2 border-dashed border-[hsl(var(--stadium-yellow))]/50 p-10 text-center" data-testid="premium-lock">
      <Lock size={30} className="mx-auto text-[hsl(var(--stadium-yellow))] mb-3" />
      <h3 className="font-display text-2xl uppercase">{title || "A Premium feature"}</h3>
      <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
        {blurb || "Upgrade to GAFFER Premium to unlock this."}
      </p>
      <Button onClick={() => setOpen(true)} data-testid="lock-upgrade-btn"
        className="mt-5 bg-[hsl(var(--stadium-yellow))] text-black hover:bg-[hsl(var(--stadium-yellow))]/90 rounded-none uppercase text-xs font-bold tracking-wide">
        <Sparkles size={14} className="me-1" /> Unlock Premium
      </Button>
      <PremiumDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
